// EntityFilterPanel.tsx - Filter component
import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Input } from "@shared/ui/input";
import {
  useEntityTypes,
  useTerritoryTypeOptions,
} from "@features/onboarding/utility-setup/hooks";
import { EntityFilters } from "@features/onboarding/utility-setup/types";

interface EntityFilterPanelProps {
  currentFilters: EntityFilters;
  onApplyFilters: (filters: Partial<EntityFilters>) => void;
  onResetFilters: () => void;
}

export const EntityFilterPanel: React.FC<EntityFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  const { data: territoryTypes, isLoading: loadingTerritoryTypes } =
    useEntityTypes();

  const form = useForm({
    defaultValues: {
      territory_type: currentFilters.territoryType || "ALL_TYPES",
      status:
        currentFilters.status !== undefined
          ? currentFilters.status.toString()
          : "ALL_STATUS",
      created_on: currentFilters.createdOn || "",
    },
  });

  // Sync form values when currentFilters change
  useEffect(() => {
    form.setValue(
      "territory_type",
      currentFilters.territoryType || "ALL_TYPES"
    );
    form.setValue(
      "status",
      currentFilters.status !== undefined
        ? currentFilters.status.toString()
        : "ALL_STATUS"
    );
    form.setValue("created_on", currentFilters.createdOn || "");
  }, [currentFilters, form]);

  const handleApplyFilters = (data: any) => {
    const filtersToApply: Partial<EntityFilters> = {};

    if (data.territory_type && data.territory_type !== "ALL_TYPES") {
      filtersToApply.territoryType = data.territory_type;
    }

    if (data.status && data.status !== "ALL_STATUS") {
      filtersToApply.status = data.status === "true";
    }

    if (data.created_on) {
      filtersToApply.createdOn = data.created_on;
    }

    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    form.reset({
      territory_type: "ALL_TYPES",
      status: "ALL_STATUS",
      created_on: "",
    });
    onResetFilters();
  };

  return (
    <form onSubmit={form.handleSubmit(handleApplyFilters)}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Territory Type */}
        <div className="space-y-2">
          <Label>Territory Type</Label>
          <Controller
            name="territory_type"
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select territory type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_TYPES">All Types</SelectItem>
                    {territoryTypes?.result?.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
          {loadingTerritoryTypes && (
            <div className="text-xs text-gray-500">Loading types...</div>
          )}
        </div>

        {/* Status */}
        <div className="space-y-2">
          <Label>Status</Label>
          <Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_STATUS">All Status</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Created On */}
        <div className="space-y-2">
          <Label>Created On</Label>
          <Controller
            name="created_on"
            control={form.control}
            render={({ field }) => (
              <Input
                type="date"
                placeholder="Select date"
                {...field}
                className="w-full"
              />
            )}
          />
        </div>

        {/* Actions */}
        <div className="col-span-1 md:col-span-3 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Button type="submit">Apply Filters</Button>
        </div>
      </div>
    </form>
  );
};
